import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SEOHead from '@/components/feature/SEOHead';
import BreadcrumbNav from '@/components/base/BreadcrumbNav';
import { categories, featuredComics } from '@/mocks/comics';

const categoryHeroImages: Record<string, string> = {
  'school-romance': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20school%20romance%20collage%20background%2C%20young%20couple%20in%20cherry%20blossom%20campus%20scene%2C%20warm%20coral%20and%20rose%20pink%20tones%2C%20soft%20dreamy%20atmosphere%2C%20dark%20overlay%2C%20romantic%20illustration%2C%20cinematic%20composition&width=1920&height=700&seq=cat-hero-school-romance&orientation=landscape',
  'fantasy': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20fantasy%20adventure%20collage%20background%2C%20magical%20world%20with%20dragons%20and%20castles%2C%20warm%20coral%20and%20golden%20tones%2C%20epic%20atmosphere%2C%20dark%20overlay%2C%20cinematic%20composition&width=1920&height=700&seq=cat-hero-fantasy&orientation=landscape',
  'action': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20action%20fighting%20collage%20background%2C%20dynamic%20battle%20scene%20with%20energy%20effects%2C%20warm%20coral%20and%20fiery%20red%20tones%2C%20intense%20atmosphere%2C%20dark%20overlay%2C%20cinematic%20composition&width=1920&height=700&seq=cat-hero-action&orientation=landscape',
  'thriller': 'https://readdy.ai/api/search-image?query=Korean%20manhwa%20style%20thriller%20mystery%20collage%20background%2C%20dark%20alley%20and%20shadowy%20figures%2C%20warm%20coral%20and%20dark%20crimson%20tones%2C%20suspenseful%20atmosphere%2C%20dark%20overlay%2C%20cinematic%20composition&width=1920&height=700&seq=cat-hero-thriller&orientation=landscape',
};

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const category = categories.find((c) => c.slug === slug);

  if (!category) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-4xl font-black text-white mb-4">分类未找到</h1>
            <Link to="/" className="text-comic-primary hover:underline">返回首页</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const categoryComics = featuredComics.filter((c) => c.category === slug);
  const otherComics = featuredComics.filter((c) => c.category !== slug).slice(0, 6);
  const heroImage = categoryHeroImages[slug] || categoryHeroImages['fantasy'];

  return (
    <>
      <SEOHead
        title={`${category.name}韩漫 - 韩国漫画网免费在线${category.name}漫画阅读`}
        description={`韩国漫画网${category.name}分类：${category.description}。海量免费${category.name}韩漫在线看，每日更新，高清画质。`}
        keywords={`${category.name}韩漫,${category.name}漫画,韩国漫画${category.name},免费${category.name}漫画,在线${category.name}漫画`}
        canonicalUrl={`http://w0ap.com/category/${slug}`}
        breadcrumbs={[
          { label: '首页', path: '/' },
          { label: category.name, path: `/category/${slug}` },
        ]}
        schema={{
          '@context': 'https://schema.org',
          '@type': 'CollectionPage',
          name: `${category.name}韩漫 - 韩国漫画网`,
          description: category.description,
          url: `http://w0ap.com/category/${slug}`,
          isPartOf: {
            '@type': 'WebSite',
            name: '韩国漫画网',
            url: 'http://w0ap.com/',
          },
        }}
      />

      <Navbar />

      <main>
        {/* 半幅Hero */}
        <section className="relative h-[60vh] min-h-[400px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={heroImage}
              alt={`${category.name}韩漫`}
              title={`${category.name}韩漫 - 韩国漫画网分类`}
              className="w-full h-full object-cover object-center"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-comic-dark"></div>
          <div className="absolute inset-0 comic-dot-pattern opacity-20"></div>
          <div className="relative z-10 text-center px-4">
            <p className="text-comic-accent text-xs font-medium tracking-widest mb-4">CATEGORY</p>
            <h1 className="text-5xl md:text-8xl font-black text-white italic tracking-tight">
              {category.name}
            </h1>
            <p className="text-gray-300 text-sm md:text-base mt-4 max-w-lg mx-auto">
              {category.description}
            </p>
          </div>
        </section>

        {/* 面包屑 */}
        <BreadcrumbNav
          items={[
            { label: '首页', path: '/' },
            { label: category.name },
          ]}
        />

        {/* 子分类导航 */}
        <section className="py-12 px-4 md:px-8 lg:px-12 comic-dot-pattern">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-2xl md:text-4xl font-black text-white">
                细分<span className="text-comic-primary">题材</span>
              </h2>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {category.subCategories.map((sub, idx) => (
                <a
                  key={sub}
                  href="#"
                  className="bg-comic-card rounded-2xl p-6 text-center hover:bg-comic-card-hover transition-all duration-300 group cursor-pointer"
                >
                  <div className="w-12 h-12 mx-auto mb-3 flex items-center justify-center rounded-full bg-comic-accent/10 text-comic-accent group-hover:bg-comic-accent group-hover:text-white transition-all">
                    <i className={`text-xl ${idx === 0 ? 'ri-heart-line' : idx === 1 ? 'ri-magic-line' : idx === 2 ? 'ri-fire-line' : 'ri-emotion-line'}`}></i>
                  </div>
                  <h3 className="text-white font-bold text-sm group-hover:text-comic-primary transition-colors">{sub}</h3>
                </a>
              ))}
            </div>
          </div>
        </section>

        {/* 该分类下的作品列表 */}
        <section className="py-12 px-4 md:px-8 lg:px-12">
          <div className="max-w-[1400px] mx-auto">
            <div className="flex items-end justify-between mb-8">
              <h2 className="text-2xl md:text-4xl font-black text-white">
                {category.name}<span className="text-comic-primary">作品</span>
              </h2>
              <p className="text-gray-500 text-sm hidden md:block">共 {categoryComics.length} 部作品</p>
            </div>

            {categoryComics.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {categoryComics.map((comic) => (
                  <Link key={comic.id} to={`/comic/${comic.id}`} className="group cursor-pointer">
                    <article className="bg-comic-card rounded-2xl overflow-hidden transition-all duration-300 hover:bg-comic-card-hover h-full">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                          src={comic.imageUrl || comic.thumbnailUrl}
                          alt={comic.title}
                          title={`${comic.title} - ${category.name}韩漫`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute top-2.5 left-2.5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                            comic.status === '连载中' ? 'bg-comic-accent text-white' : 'bg-comic-cyan text-black'
                          }`}>
                            {comic.status}
                          </span>
                        </div>
                        <div className="absolute top-2.5 right-2.5">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-bold ${
                            comic.isFree ? 'bg-comic-cyan/90 text-black' : 'bg-comic-accent/90 text-white'
                          }`}>
                            {comic.isFree ? 'FREE' : 'HOT'}
                          </span>
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 pt-8">
                          <h3 className="text-white font-bold text-sm mb-1 group-hover:text-comic-primary transition-colors">
                            {comic.title}
                          </h3>
                          <p className="text-gray-300 text-xs">{comic.author}</p>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-20">
                <p className="text-gray-500 text-lg">该分类暂无作品，敬请期待！</p>
              </div>
            )}
          </div>
        </section>

        {/* 更多推荐 */}
        {otherComics.length > 0 && (
          <section className="py-12 px-4 md:px-8 lg:px-12 comic-dot-pattern">
            <div className="max-w-[1400px] mx-auto">
              <div className="flex items-end justify-between mb-8">
                <h2 className="text-2xl md:text-4xl font-black text-white">
                  猜你<span className="text-comic-primary">喜欢</span>
                </h2>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-5">
                {otherComics.map((comic) => (
                  <Link key={comic.id} to={`/comic/${comic.id}`} className="group cursor-pointer">
                    <article className="bg-comic-card rounded-2xl overflow-hidden transition-all duration-300 hover:bg-comic-card-hover h-full">
                      <div className="relative aspect-[3/4] overflow-hidden">
                        <img
                          src={comic.imageUrl || comic.thumbnailUrl}
                          alt={comic.title}
                          title={`${comic.title} - 韩国漫画网推荐`}
                          className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/60 to-transparent p-4 pt-8">
                          <h3 className="text-white font-bold text-sm mb-1 group-hover:text-comic-primary transition-colors">
                            {comic.title}
                          </h3>
                          <p className="text-gray-300 text-xs">{comic.categoryName}</p>
                        </div>
                      </div>
                    </article>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}